import { useEffect, useState } from 'react'
import { Database, KeyRound, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import DataBindingDialog from '@/frontend/features/Fde/DataBindingDialog'
import FdeRecordStatus from '@/frontend/features/Fde/FdeRecordStatus'
import SemanticObjectPreview from '@/frontend/features/Fde/SemanticObjectPreview'
import SemanticObject360 from '@/frontend/features/Fde/data/SemanticObject360'
import type { OntologyInspectorProps } from '@/frontend/features/Fde/OntologyGraphInspector'
import type { DataBinding } from '@/frontend/features/Fde/types'

type EditorState = { open: boolean; record: DataBinding | null; deleteMode: boolean }

export default function OntologyInspectorData(props: OntologyInspectorProps) {
  const [editor, setEditor] = useState<EditorState>({ open: false, record: null, deleteMode: false })
  const [selectedObject, setSelectedObject] = useState<{ classId: string; objectId: string } | null>(null)
  const bindings = props.dataBindings.filter((binding) => props.selectedNode
    ? String(binding.ontology_class?.id) === String(props.selectedNode.ontology_class?.id)
    : String(binding.link_type?.id) === String(props.selectedRelationship?.link_type.id))
  const canHydrate = Boolean(props.selectedNode)
    && bindings.some((binding) => binding.binding_kind === 'object_type')
    && bindings.some((binding) => binding.binding_kind === 'property' && binding.identity_role === 'primary_key')
  const sources = new Set(bindings.map((binding) => binding.data_asset?.name).filter(Boolean)).size
  const identity = bindings.find((binding) => binding.identity_role === 'primary_key')

  useEffect(() => setSelectedObject(null), [props.selectedNode?.id, props.selectedRelationship?.id])

  if (selectedObject) return <SemanticObject360 projectId={props.projectId} classId={selectedObject.classId} objectId={selectedObject.objectId} copy={props.copy.data} onBack={() => setSelectedObject(null)} onOpenObject={(classId, objectId) => setSelectedObject({ classId, objectId })} />

  return <div className="space-y-4">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{props.copy.workspace.data_mapping_title}</h3>
        <p className="mt-1 max-w-xl text-xs leading-5 text-muted-foreground">{props.copy.workspace.data_mapping_description}</p>
        {bindings.length > 0 && <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          <span><strong className="font-semibold text-foreground">{bindings.length}</strong> {props.copy.workspace.data_mapping_count}</span>
          <span><strong className="font-semibold text-foreground">{sources}</strong> {props.copy.workspace.data_mapping_sources_count}</span>
          {identity && <span className="inline-flex items-center gap-1"><KeyRound className="size-3" />{identity.semantic_target}</span>}
        </p>}
      </div>
      {props.canManage && <Button size="sm" onClick={() => setEditor({ open: true, record: null, deleteMode: false })}><Plus />{props.copy.workspace.data_mapping_add}</Button>}
    </div>

    {canHydrate && props.selectedNode?.ontology_class?.id && <SemanticObjectPreview projectId={props.projectId} classId={props.selectedNode.ontology_class.id} copy={props.copy} limit={15} onSelect={(object) => setSelectedObject({ classId: String(props.selectedNode!.ontology_class!.id), objectId: object.id })} />}

    {bindings.length === 0
      ? <div className="rounded-xl border border-dashed border-border px-6 py-12 text-center"><Database className="mx-auto size-6 text-muted-foreground" /><p className="mt-3 text-sm text-muted-foreground">{props.copy.workspace.data_mapping_empty}</p></div>
      : <BindingContract bindings={bindings} props={props} onEdit={(record, deleteMode) => setEditor({ open: true, record, deleteMode })} />}

    <DataBindingDialog open={editor.open} onOpenChange={(open) => setEditor((current) => ({ ...current, open }))} record={editor.record} deleteMode={editor.deleteMode} {...props} />
  </div>
}

function BindingContract({ bindings, props, onEdit }: { bindings: DataBinding[]; props: OntologyInspectorProps; onEdit: (record: DataBinding, deleteMode: boolean) => void }) {
  return <section className="overflow-hidden rounded-xl border border-border bg-background">
    <div className="grid grid-cols-[minmax(10rem,1.1fr)_minmax(10rem,1fr)_8rem_auto] gap-3 border-b border-border bg-muted/25 px-3 py-2 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
      <span>{props.copy.workspace.data_mapping_semantic_column}</span>
      <span>{props.copy.workspace.data_mapping_source_column}</span>
      <span>{props.copy.workspace.data_mapping_method}</span>
      <span className="sr-only">{props.copy.common.actions}</span>
    </div>
    <div className="divide-y divide-border/70">
      {bindings.map((binding) => <div key={binding.id} className="grid grid-cols-[minmax(10rem,1.1fr)_minmax(10rem,1fr)_8rem_auto] items-center gap-3 px-3 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-xs font-medium text-foreground">{semanticLabel(binding, props)}</span>
            <FdeRecordStatus status={binding.status} proposedLabel={props.copy.workspace.proposed} confirmedLabel={props.copy.workspace.confirmed_status} showConfirmed />
          </div>
          {binding.description && <p className="mt-0.5 truncate text-[10px] text-muted-foreground" title={binding.description}>{binding.description}</p>}
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs text-foreground">{binding.data_asset?.name || props.copy.workspace.data_mapping_semantic_join}</p>
          {binding.source_fields.length > 0 && <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground" title={binding.source_fields.join(', ')}>{binding.source_fields.join(', ')}</p>}
        </div>
        <div className="min-w-0 text-[10px] text-muted-foreground">
          <p className="truncate">{props.copy.workspace[`data_mapping_method_${binding.mapping_method}`] || binding.mapping_method}</p>
          {binding.identity_role !== 'none' && <p className="mt-0.5 truncate text-primary">{props.copy.workspace[`data_mapping_identity_${binding.identity_role}`] || binding.identity_role}</p>}
        </div>
        {props.canManage ? <div className="flex gap-0.5">
          <Button size="icon-sm" variant="ghost" onClick={() => onEdit(binding, false)} aria-label={props.copy.common.edit}><Pencil /></Button>
          <Button size="icon-sm" variant="ghost" onClick={() => onEdit(binding, true)} aria-label={props.copy.common.delete}><Trash2 /></Button>
        </div> : <span />}
      </div>)}
    </div>
  </section>
}

function semanticLabel(binding: DataBinding, props: OntologyInspectorProps) {
  if (binding.binding_kind === 'object_type') return props.copy.workspace.data_mapping_target_object
  const prefix = binding.ontology_class ? `${binding.ontology_class.name}.` : ''
  return prefix && binding.semantic_target.startsWith(prefix) ? binding.semantic_target.slice(prefix.length) : binding.semantic_target
}
